import { useRef, useEffect, useCallback } from 'react';
import { sanitizeEmailHtml } from '../../utils/sanitizeEmailHtml';
import styles from './EmailHtmlViewer.module.css';

interface EmailHtmlViewerProps {
  /** Raw HTML string from the email */
  html: string;
  /** Optional className override for the iframe wrapper */
  className?: string;
}

/**
 * Renders email HTML inside a sandboxed iframe with automatic height resizing.
 *
 * Uses sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
 * so we can read contentDocument.scrollHeight for auto-sizing, while still
 * blocking script execution. allow-popups lets links open in new tabs via
 * the injected <base target="_blank"> tag.
 *
 * Injects a small base stylesheet to ensure readable defaults when the email
 * HTML doesn't carry its own styles.
 */
const BASE_STYLE = `
<style>
  body {
    margin: 0;
    padding: 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 16px;
    line-height: 1.6;
    color: #333;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  blockquote {
    margin: 0.5em 0 0.5em 0;
    padding-left: 1em;
    border-left: 2px solid #ccc;
    color: #666;
  }
  pre { white-space: pre-wrap; word-wrap: break-word; }
  a { color: #0369a1; }
</style>
`;

const EmailHtmlViewer: React.FC<EmailHtmlViewerProps> = ({ html, className }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;

      // Reset height so scrollHeight is calculated correctly
      iframe.style.height = '0px';
      const height = doc.documentElement.scrollHeight || doc.body.scrollHeight;
      iframe.style.height = `${Math.max(height, 100)}px`;
    } catch {
      // Cross-origin restriction — fall back to min-height via CSS
    }
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      resizeIframe();

      // Observe content size changes (images loading, etc.)
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;

        const observer = new ResizeObserver(() => resizeIframe());
        observer.observe(doc.body);

        // Also handle images loading after initial render
        const images = doc.querySelectorAll('img');
        images.forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', resizeIframe);
            img.addEventListener('error', resizeIframe);
          }
        });

        return () => observer.disconnect();
      } catch {
        // Ignore cross-origin errors
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [html, resizeIframe]);

  const sanitized = sanitizeEmailHtml(html);
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener noreferrer">${BASE_STYLE}</head><body>${sanitized}</body></html>`;

  return (
    <iframe
      ref={iframeRef}
      className={`${styles.viewer} ${className || ''}`}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      title="Email content"
    />
  );
};

export default EmailHtmlViewer;
